'use client'

import { useEffect, useState } from 'react'

import { getVehicleTypes, VehicleType } from '@/app/[locale]/[ferry]/_actions/getVehicleTypes'

export interface VehicleDimensions {
  height: number
  length: number
}

export interface VehicleDimensionsState {
  dimensions?: VehicleDimensions
  loading: boolean
}

// Deduped across all callers: the vehicle-type list is identical app-wide and cached server-side.
let vehicleTypesPromise: Promise<VehicleType[]> | undefined

const loadVehicleTypes = (): Promise<VehicleType[]> => {
  if (!vehicleTypesPromise) {
    vehicleTypesPromise = getVehicleTypes().catch((error) => {
      vehicleTypesPromise = undefined // Allow a retry on the next call rather than caching the rejection.
      throw error
    })
  }
  return vehicleTypesPromise
}

// Resolves a vehicle/trailer type code to its dimensions. On failure `loading` becomes false with no
// dimensions, so callers can render the vehicle name without the size instead of a permanent skeleton.
export const useVehicleDimensions = (code: string | undefined): VehicleDimensionsState => {
  const [state, setState] = useState<VehicleDimensionsState>({ loading: Boolean(code) })

  useEffect(() => {
    if (!code) {
      setState({ loading: false })
      return
    }
    let active = true
    setState({ loading: true })
    loadVehicleTypes()
      .then((types) => {
        if (!active) return
        const match = types.find((v) => v.code === code)
        setState({ dimensions: { height: match?.height ?? 0, length: match?.length ?? 0 }, loading: false })
      })
      .catch(() => {
        if (!active) return
        setState({ loading: false })
      })
    return () => {
      active = false
    }
  }, [code])

  return state
}
